import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { getExtensionDependencyFromEditor } from "@lexical/extension";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  $generateNodesFromMarkdownString,
} from "@lexical/markdown";
import {
  $addUpdateTag,
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isRootNode,
  $setSelection,
  type LexicalNode,
  FORMAT_TEXT_COMMAND,
  HISTORY_PUSH_TAG,
  REDO_COMMAND,
  UNDO_COMMAND,
  type LexicalEditor,
} from "lexical";
import {
  $deleteBlocks,
  $duplicateBlocks,
  $findBlocks,
  $moveBlocks,
  $moveBlocksBy,
} from "../features/block-id/operations";
import { $getBlockId } from "../features/block-id/state";
import { BlockSelectionExtension } from "../features/block-selection/extension";
import {
  $canInsertBlocks,
  $fragmentOptions,
  $paragraphsFromText,
} from "../features/clipboard/extension";
import { $insertFragment, $serializeNodes } from "../features/clipboard/fragment";
import { createTransformers } from "../features/markdown/transformers";
import { SuggestionsExtension } from "../features/suggestions/extension";
import { $typeTrigger } from "../features/suggestions/open";
import { $insertToken } from "../features/tokens/insert";
import {
  ADD_ATTACHMENTS_COMMAND,
  SET_ATTACHMENT_ALT_COMMAND,
  type AttachmentOutcome,
  type SetAttachmentAltPayload,
} from "../features/attachments/commands";
import {
  DEFAULT_TABLE,
  EDIT_TABLE_COMMAND,
  INSERT_TABLE_COMMAND,
  type TableOutcome,
} from "../features/tables/commands";
import { $isTokenNode } from "../features/tokens/node";
import { checkFragment, exceedsBytes } from "../core/clipboard";
import {
  ATTACHMENT_NODE_TYPE,
  attachmentLostWarning,
  checkUrl,
  CORE_REGISTRY,
  CORE_TOKEN_REGISTRY,
  createDocument,
  identityLostWarning,
  readAttachment,
  UNSPECIFIED_ENGINE_VERSION,
  type BlockId,
  type CapabilityCommand,
  type CapabilityOverrides,
  type ExportWarning,
  type InlineToken,
  type SerializedNodeLike,
  type TokenRegistry,
  type DocumentId,
  type EditorDocument,
  type EditorPreset,
  type ExportResult,
  type UrlPolicy,
} from "../index";
import { $runCommand } from "../lexical/commands";
import { PROGRAMMATIC_TAG } from "../lexical/tags";
import type { SubmitOutcome } from "./composer";
import { readSelectionInfo } from "../features/host/selection";
import type {
  DispatchResult,
  EditorIntent,
  EditTarget,
  RejectionReason,
  SelectionInfo,
} from "./contract";

export type {
  DispatchResult,
  EditorHandle,
  EditorIntent,
  EditTarget,
  InlineFormat,
  RejectionReason,
  SelectionInfo,
  SelectionState,
  SnapshotResult,
} from "./contract";

export interface LiveEditor {
  selectedBlockIds(): readonly BlockId[];
  selectionInfo(): SelectionInfo | null;
  dispatch(intent: EditorIntent): DispatchResult;
  pasteAsMarkdown(): Promise<DispatchResult>;
  snapshot(): EditorDocument;
  markdown(): ExportResult;
  plainText(): string;
}

export interface LiveEditorOptions {
  editor: LexicalEditor;
  documentId: DocumentId;
  preset: EditorPreset;
  urlPolicy: UrlPolicy;
  tokens?: TokenRegistry;
  /** What this editor offers besides its preset's defaults. */
  offers?: CapabilityOverrides;
  getEditSequence: () => number;
  /** The composer's attachments, where it has a tray. */
  tray?: { remove(assetIds: readonly string[]): void } | null;
  /** The composer's submit path, when there is one. A holder, because it is set after this. */
  submit?: { current: (() => SubmitOutcome) | null };
}

export const engineVersion = (editor: LexicalEditor): string =>
  (editor.constructor as typeof LexicalEditor).version ?? UNSPECIFIED_ENGINE_VERSION;

/**
 * Nobody handles a table command where table support is not loaded, which is an editor that
 * neither offers tables nor holds one: there is no table to edit and none to be made.
 */
function tableOutcome(
  handled: boolean,
  outcome: TableOutcome,
): { ok: true } | { ok: false; reason: RejectionReason } {
  if (!handled) return { ok: false, reason: "not-offered" };
  return outcome.refused === null ? { ok: true } : { ok: false, reason: outcome.refused };
}

export function createLiveEditor(options: LiveEditorOptions): LiveEditor {
  const { editor, documentId, preset, urlPolicy, getEditSequence } = options;
  const tokens = options.tokens ?? CORE_TOKEN_REGISTRY;
  const commandFor = (id: string): CapabilityCommand | undefined =>
    CORE_REGISTRY.command(preset, id, options.offers);
  const validateUrl = (url: string): boolean => checkUrl(url, urlPolicy).ok;
  const transformers = createTransformers(preset, validateUrl, tokens);

  const runOrRefuse = (
    command: CapabilityCommand,
  ): { ok: true } | { ok: false; reason: RejectionReason } =>
    $runCommand(command) ? { ok: true } : { ok: false, reason: "invalid-target" };

  /** Converts each block where it stands, then puts the selection back where it was. */
  const $turnBlocksInto = (
    blockIds: readonly BlockId[],
    command: CapabilityCommand,
  ): { ok: true } | { ok: false; reason: RejectionReason } => {
    const found = $findBlocks(blockIds);
    if (!found.ok) return found;
    const before = $getSelection()?.clone() ?? null;
    for (const block of found.nodes) {
      if (!block.isAttached() || !$isElementNode(block)) continue;
      block.selectStart();
      $runCommand(command);
    }
    const stillThere = before !== null && before.getNodes().every((node) => node.isAttached());
    if (stillThere) $setSelection(before);
    return { ok: true };
  };

  const insertToken = (token: InlineToken): DispatchResult => {
    if (tokens.check(token) !== null) return { status: "rejected", reason: "invalid-token" };
    return structural(() => $insertToken(token));
  };

  /** One warning for everything whose Markdown no longer says what it was: tokens, attachments. */
  const $exportWarnings = (): ExportWarning[] => {
    const warnings: ExportWarning[] = [];
    for (const block of $getRoot().getChildren()) {
      const blockId = $getBlockId(block) ?? undefined;
      // By type and through JSON: the node's class loads on demand, and this file does not.
      if (block.getType() === ATTACHMENT_NODE_TYPE) {
        const attachment = readAttachment(block.exportJSON() as SerializedNodeLike);
        if (attachment !== undefined) warnings.push(attachmentLostWarning(attachment, blockId));
      }
      if (!$isElementNode(block)) continue;
      for (const node of block.getAllTextNodes()) {
        const token = $isTokenNode(node) ? node.getToken() : null;
        if (token !== null && tokens.markdown(token).lossy) {
          warnings.push(identityLostWarning(token, blockId));
        }
      }
    }
    return warnings;
  };

  /**
   * Runs a structural operation as one undo step. The operation reports a refusal before it has
   * changed anything, so a refused request leaves the document and its history as they were.
   */
  const structural = (
    operate: () => { ok: true; silent?: boolean } | { ok: false; reason: RejectionReason },
    tag?: string,
  ): DispatchResult => {
    let result: DispatchResult = { status: "applied" };
    editor.update(
      () => {
        const outcome = operate();
        if (outcome.ok && outcome.silent === true) return;
        if (outcome.ok) $addUpdateTag(HISTORY_PUSH_TAG);
        else result = { status: "rejected", reason: outcome.reason };
      },
      { discrete: true, ...(tag === undefined ? {} : { tag }) },
    );
    return result;
  };

  const $applyToBlock = (
    target: Extract<EditTarget, { kind: "block" }>,
    markdown: string,
  ): { ok: true } | { ok: false; reason: RejectionReason } => {
    const found = $findBlocks([target.blockId]);
    if (!found.ok) return found;
    const block = found.nodes[0]!;
    if (!$isRootNode(block.getParent())) return { ok: false, reason: "invalid-target" };
    const nodes = $generateNodesFromMarkdownString(markdown, transformers);
    if (target.position === "after") {
      let after = block;
      for (const node of nodes) after = after.insertAfter(node);
      return { ok: true };
    }
    for (const node of nodes) block.insertBefore(node);
    if (target.position === "replace") {
      block.remove();
      if ($getRoot().getChildrenSize() === 0) $getRoot().append($createParagraphNode());
    }
    return { ok: true };
  };

  const $replaceBlocks = (
    blockIds: readonly BlockId[],
    markdown: string,
  ): { ok: true } | { ok: false; reason: RejectionReason } => {
    const found = $findBlocks(blockIds);
    if (!found.ok) return found;
    const first = found.nodes[0];
    if (first === undefined) return { ok: false, reason: "invalid-target" };
    if (found.nodes.some((node) => !$isRootNode(node.getParent()))) {
      return { ok: false, reason: "invalid-target" };
    }
    for (const node of $generateNodesFromMarkdownString(markdown, transformers)) {
      first.insertBefore(node);
    }
    for (const node of found.nodes) node.remove();
    if ($getRoot().getChildrenSize() === 0) $getRoot().append($createParagraphNode());
    return { ok: true };
  };

  /**
   * The gate a paste goes through, for text the caller supplies. `generate` makes the nodes, and
   * runs only once the text is known to be small enough to read.
   */
  const $insertChecked = (
    source: string,
    generate: () => LexicalNode[],
    intoCode: boolean,
  ): { ok: true; silent?: boolean } | { ok: false; reason: RejectionReason } => {
    const selection = $getSelection();
    // Text can go into a code block, where it stays text. Anything with structure cannot.
    const allowed = intoCode
      ? $isRangeSelection(selection) || $isNodeSelection(selection)
      : $canInsertBlocks(selection);
    if (selection === null || !allowed) return { ok: false, reason: "invalid-target" };
    const options = $fragmentOptions();
    if (exceedsBytes(source, options.limits.maxBytes)) return { ok: false, reason: "too-large" };
    const generated = generate();
    const fragment = checkFragment($serializeNodes(generated), {
      ...options,
      sourceDocumentId: null,
    });
    if (fragment.status === "too-large") return { ok: false, reason: "too-large" };
    if (fragment.status === "declined") return { ok: false, reason: "invalid-target" };
    const outcome = $insertFragment(fragment, selection, options.limits);
    if (outcome === "too-deep" || outcome === "table-too-large") {
      return { ok: false, reason: "too-large" };
    }
    // Nothing to insert is not an error, and not an undo step either.
    return outcome === "inserted" ? { ok: true } : { ok: true, silent: true };
  };

  const blockSelection = getExtensionDependencyFromEditor(editor, BlockSelectionExtension).output;
  const suggestions = getExtensionDependencyFromEditor(editor, SuggestionsExtension).output;

  const focus = (): void => {
    editor.focus();
    // Lexical focuses by restoring its DOM selection, and a block selection has none.
    const root = editor.getRootElement();
    if (root !== null && root.ownerDocument.activeElement !== root) {
      root.focus({ preventScroll: true });
    }
  };

  const dispatch = (intent: EditorIntent): DispatchResult => {
    if (intent.type === "focus") {
      if (intent.at !== undefined) {
        const at = intent.at;
        // Moving the caret is not an edit: it dirties nothing and adds nothing to history.
        editor.update(() => (at === "end" ? $getRoot().selectEnd() : $getRoot().selectStart()), {
          discrete: true,
        });
      }
      focus();
      return { status: "applied" };
    }
    if (intent.type === "select-blocks") {
      // Selecting is not editing: it works while read-only and adds nothing to history.
      if (intent.blockIds.length === 0) blockSelection.clear();
      else blockSelection.select(intent.blockIds);
      return { status: "applied" };
    }
    if (!editor.isEditable()) return { status: "rejected", reason: "read-only" };
    switch (intent.type) {
      case "undo":
        editor.dispatchCommand(UNDO_COMMAND, undefined);
        break;
      case "redo":
        editor.dispatchCommand(REDO_COMMAND, undefined);
        break;
      case "format-text":
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, intent.format);
        break;
      case "set-block": {
        const command = commandFor(intent.block);
        if (command === undefined) return { status: "rejected", reason: "not-offered" };
        return structural(() => runOrRefuse(command));
      }
      case "set-link":
        if (intent.url !== null && !validateUrl(intent.url)) {
          return { status: "rejected", reason: "unsafe-url" };
        }
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, intent.url);
        break;
      case "insert-divider":
      case "run-command": {
        const id = intent.type === "run-command" ? intent.commandId : "divider";
        const command = commandFor(id);
        if (command === undefined) return { status: "rejected", reason: "not-offered" };
        return structural(() => runOrRefuse(command));
      }
      case "turn-into": {
        const command = commandFor(intent.block);
        if (command === undefined) return { status: "rejected", reason: "not-offered" };
        return structural(() => $turnBlocksInto(intent.blockIds, command));
      }
      case "insert-reference":
        return insertToken({ kind: "entity", value: intent.reference });
      case "insert-token":
        return insertToken(intent.token);
      case "move-blocks":
        return structural(() => $moveBlocks(intent.blockIds, intent.beforeId));
      case "move-blocks-by":
        return structural(() => {
          const outcome = $moveBlocksBy(intent.blockIds, intent.direction);
          // Already at the edge: report it as applied, but leave history alone.
          return outcome.ok && !outcome.moved ? { ok: true, silent: true } : outcome;
        });
      case "duplicate-blocks":
        return structural(() => $duplicateBlocks(intent.blockIds));
      case "delete-blocks":
        return structural(() => $deleteBlocks(intent.blockIds));
      case "insert-markdown":
        return structural(() =>
          $insertChecked(
            intent.markdown,
            () => $generateNodesFromMarkdownString(intent.markdown, transformers),
            false,
          ),
        );
      case "insert-text":
        return structural(() =>
          $insertChecked(intent.text, () => $paragraphsFromText(intent.text), true),
        );
      case "open-suggestions": {
        // First, because an editor nobody has been in has no selection to type at, and because the
        // menu closes when the editor is not where the keyboard is.
        focus();
        return structural(() => $typeTrigger(intent.trigger, suggestions.engine.rules));
      }
      case "insert-attachment": {
        // Not an undo step, and not an edit: nothing is in the document until the adapter answers.
        const outcome: AttachmentOutcome = { refused: null, accepted: 0 };
        const handled = editor.dispatchCommand(ADD_ATTACHMENTS_COMMAND, {
          files: intent.files,
          outcome,
        });
        const refused = handled ? outcome.refused : "not-offered";
        return refused === null ? { status: "applied" } : { status: "rejected", reason: refused };
      }
      case "remove-attachment": {
        if (options.tray === undefined || options.tray === null) {
          return { status: "rejected", reason: "not-offered" };
        }
        options.tray.remove([intent.assetId]);
        break;
      }
      case "set-attachment-alt": {
        const outcome: SetAttachmentAltPayload["outcome"] = { refused: null };
        return structural(() => {
          const handled = editor.dispatchCommand(SET_ATTACHMENT_ALT_COMMAND, {
            target: { blockId: intent.blockId },
            alt: intent.alt,
            outcome,
          });
          if (!handled) return { ok: false, reason: "not-offered" };
          return outcome.refused === null ? { ok: true } : { ok: false, reason: outcome.refused };
        });
      }
      case "insert-table": {
        if (commandFor("table") === undefined) return { status: "rejected", reason: "not-offered" };
        const outcome: TableOutcome = { refused: null };
        return structural(() =>
          tableOutcome(
            editor.dispatchCommand(INSERT_TABLE_COMMAND, {
              rows: intent.rows ?? DEFAULT_TABLE.rows,
              columns: intent.columns ?? DEFAULT_TABLE.columns,
              headerRow: intent.headerRow ?? DEFAULT_TABLE.headerRow,
              outcome,
            }),
            outcome,
          ),
        );
      }
      case "edit-table": {
        const outcome: TableOutcome = { refused: null };
        return structural(() =>
          tableOutcome(
            editor.dispatchCommand(EDIT_TABLE_COMMAND, { edit: intent.edit, outcome }),
            outcome,
          ),
        );
      }
      case "submit": {
        const outcome = options.submit?.current?.() ?? "not-offered";
        return outcome === "sent" ? { status: "applied" } : { status: "rejected", reason: outcome };
      }
      case "apply-edit": {
        const currentEditSequence = getEditSequence();
        if (intent.expectedEditSequence !== currentEditSequence) {
          return { status: "conflict", currentEditSequence };
        }
        const target = intent.target;
        if (target.kind === "block") {
          return structural(() => $applyToBlock(target, intent.markdown), PROGRAMMATIC_TAG);
        }
        if (target.kind === "blocks") {
          return structural(
            () => $replaceBlocks(target.blockIds, intent.markdown),
            PROGRAMMATIC_TAG,
          );
        }
        editor.update(
          () => {
            $addUpdateTag(HISTORY_PUSH_TAG);
            if (target.kind === "document") {
              $convertFromMarkdownString(intent.markdown, transformers);
            } else {
              $getRoot().append(...$generateNodesFromMarkdownString(intent.markdown, transformers));
            }
          },
          { tag: PROGRAMMATIC_TAG, discrete: true },
        );
        break;
      }
    }
    return { status: "applied" };
  };

  return {
    selectedBlockIds: () => blockSelection.getState().selected,
    selectionInfo: () =>
      readSelectionInfo(editor, transformers, blockSelection.getState().selected),
    dispatch,
    async pasteAsMarkdown() {
      if (!editor.isEditable()) return { status: "rejected", reason: "read-only" };
      let text: string;
      try {
        text = await navigator.clipboard.readText();
      } catch {
        return { status: "rejected", reason: "clipboard-unavailable" };
      }
      // The browser may have asked the user first; the editor can be gone by the time it answers.
      if (editor.getRootElement() === null) return { status: "rejected", reason: "not-ready" };
      return dispatch({ type: "insert-markdown", markdown: text });
    },
    snapshot: () =>
      createDocument(documentId, editor.getEditorState().toJSON(), engineVersion(editor)),
    markdown: () =>
      editor.getEditorState().read(() => ({
        text: $convertToMarkdownString(transformers),
        warnings: $exportWarnings(),
      })),
    plainText: () => editor.getEditorState().read(() => $getRoot().getTextContent()),
  };
}
